import { normalizeSearchText } from "@gioia365/domain/search-text";
import { useEffect, useId, useRef, useState } from "react";
import { Button } from "../../design/components/Button";
import { Drawer } from "../../design/components/Drawer";
import { Icon } from "../../design/components/Icon";
import { useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { FilterControls } from "./FilterControls";
import { conditionText, FILTER_GROUP_ORDER, type FilterField } from "./filter-text";
import { completeCondition, definitionOf, type DraftCondition } from "./filter-url";

// Il pannello «Filtri» (mock-up `setupFilterBar`, `panel` e `row`): un `Drawer` a destra (a tutta larghezza sotto i
// 720 px) con «Cerca un filtro», i filtri per gruppi e una riga per filtro. Ogni riga è un pulsante con il nome del
// campo e il riassunto della scelta («tutti» o «tutte» senza scelta); aperta mostra le scelte, una riga alla volta. In
// fondo quanti filtri valgono, il numero dei risultati (la pagina dietro è inerte mentre il pannello è aperto),
// «Azzera i filtri» e «Fatto».

type Props = {
  tab: TabContext;
  /** L'`id` del contenuto, per l'`aria-controls` del pulsante «Filtri». */
  panelId: string;
  fields: readonly FilterField[];
  draft: readonly DraftCondition[];
  expanded: string | null;
  onExpand: (ref: string | null) => void;
  onDraftChange: (next: DraftCondition[]) => void;
  onReset: () => void;
  onClose: () => void;
  labelsFor: (field: FilterField) => ReadonlyMap<string, string>;
  formatDay: (day: string) => string;
  resultText: string | undefined;
  narrowed: boolean;
};

export function FilterPanel({ tab, panelId, fields, draft, expanded, onExpand, onDraftChange, onReset, onClose, labelsFor, formatDay, resultText, narrowed }: Props) {
  const t = useT();
  const baseId = useId();
  const findId = `${baseId}-find`;
  const findRef = useRef<HTMLInputElement>(null);
  const [find, setFind] = useState("");
  const headId = (ref: string) => `${baseId}-head-${ref}`;

  // Si comincia da «Cerca un filtro», come nel mock-up (la finestra altrimenti dà il fuoco a «Chiudi»); aperto da un
  // filtro attivo, dalla sua riga. Soltanto all'apertura: la riga scelta allora è `startAt`.
  const [startAt] = useState(expanded);
  useEffect(() => {
    const row = startAt === null ? null : document.getElementById(`${baseId}-head-${startAt}`);
    (row ?? findRef.current)?.focus();
  }, [baseId, startAt]);

  const key = normalizeSearchText(find);
  const visible = key === "" ? fields : fields.filter((field) => normalizeSearchText(field.label).includes(key));
  const groups = FILTER_GROUP_ORDER.map((group) => ({ group, fields: visible.filter((field) => field.group === group) })).filter(
    (entry) => entry.fields.length > 0,
  );
  const active = definitionOf(draft).conditions;
  const activeText = active.length === 1 ? t("leadFilters.activeOne") : t("leadFilters.activeMany", { count: active.length });

  function change(next: DraftCondition) {
    const index = draft.findIndex((condition) => condition.field === next.field);
    onDraftChange(index === -1 ? [...draft, next] : draft.map((condition, position) => (position === index ? next : condition)));
  }

  function remove(ref: string) {
    onDraftChange(draft.filter((condition) => condition.field !== ref));
  }

  function reset() {
    onReset();
    findRef.current?.focus();
  }

  const footer = (
    <>
      <p className="g-fpanel__foot-line">
        <span className="g-fpanel__foot-text">{active.length > 0 ? t("leadFilters.allTogether", { active: activeText }) : t("leadFilters.none")}</span>
        {resultText ? (
          <strong className="g-fpanel__result" role="status" aria-live="polite">
            {resultText}
          </strong>
        ) : null}
      </p>
      {active.length > 0 ? <Button onClick={reset}>{t("leadFilters.reset")}</Button> : null}
      <Button variant="primary" onClick={onClose}>{t("leadFilters.done")}</Button>
    </>
  );

  return (
    <Drawer open title={t("leadFilters.button")} onClose={onClose} footer={footer}>
      <div id={panelId} className="g-fpanel">
        <label className="g-visually-hidden" htmlFor={findId}>{t("leadFilters.find")}</label>
        <input
          ref={findRef}
          id={findId}
          className="g-field__input g-fpanel__find"
          type="search"
          value={find}
          autoComplete="off"
          placeholder={t("leadFilters.find")}
          onChange={(event) => setFind(event.target.value)}
        />
        {groups.length === 0 ? <p className="g-fpanel__hint">{t("leadFilters.noFieldMatches", { q: find.trim() })}</p> : null}
        {groups.map(({ group, fields: groupFields }) => {
          const headingId = `${baseId}-${group}`;
          return (
            <section key={group} className="g-fpanel__group" aria-labelledby={headingId}>
              <h3 id={headingId} className="g-fpanel__group-title">{t(`leadFilters.groups.${group}`)}</h3>
              {groupFields.map((field) => (
                <FilterRow
                  key={field.ref}
                  tab={tab}
                  field={field}
                  draft={draft.find((condition) => condition.field === field.ref)}
                  headId={headId(field.ref)}
                  open={expanded === field.ref}
                  onToggle={() => onExpand(expanded === field.ref ? null : field.ref)}
                  onChange={change}
                  onRemove={() => remove(field.ref)}
                  labels={labelsFor(field)}
                  formatDay={formatDay}
                  narrowed={narrowed || active.some((condition) => condition.field !== field.ref)}
                />
              ))}
            </section>
          );
        })}
      </div>
    </Drawer>
  );
}

type RowProps = {
  tab: TabContext;
  field: FilterField;
  draft: DraftCondition | undefined;
  headId: string;
  open: boolean;
  onToggle: () => void;
  onChange: (next: DraftCondition) => void;
  onRemove: () => void;
  labels: ReadonlyMap<string, string>;
  formatDay: (day: string) => string;
  narrowed: boolean;
};

function FilterRow({ tab, field, draft, headId, open, onToggle, onChange, onRemove, labels, formatDay, narrowed }: RowProps) {
  const t = useT();
  const bodyId = useId();
  const headRef = useRef<HTMLButtonElement>(null);
  const complete = draft ? completeCondition(draft) : null;
  const summary = complete ? conditionText(t, field, complete, labels, formatDay) : field.all;

  function remove() {
    onRemove();
    // Il pulsante appena premuto sparisce: il fuoco torna sulla riga, che resta aperta.
    headRef.current?.focus();
  }

  return (
    <div className="g-fpanel__row" data-on={complete ? "true" : undefined}>
      <button ref={headRef} id={headId} type="button" className="g-fpanel__head" aria-expanded={open} aria-controls={open ? bodyId : undefined} onClick={onToggle}>
        <span className="g-fpanel__name">{field.label}</span>{" "}
        <strong className="g-fpanel__summary">{summary}</strong>
        <Icon name={open ? "up" : "down"} />
      </button>
      {open ? (
        <div id={bodyId} className="g-fpanel__body">
          <FilterControls tab={tab} field={field} draft={draft} onChange={onChange} labels={labels} narrowed={narrowed} />
          {complete ? (
            <Button variant="ghost" onClick={remove}>{t("leadFilters.removeThis")}</Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
